import { createContext, useContext, type ReactNode } from 'react';

import type {
  PublicBoost,
  VoucherResponseDto,
  BonusDefinition,
} from '~/domains/promotions/types';
import type { PropPickProps } from '~/domains/contest/domains/common/types/propPickProps.type';
import type { PromotionActionLocation } from '~/domains/promotions/constants/analytics-enums';

import { usePromotionsProvider } from './usePromotionsProvider';

export type PromotionValidationErrors = {
  boost?: string;
  voucher?: string;
};

export type ValidatePromotionOptions = {
  picks?: PropPickProps[];
  amount?: number;
  validateBoost?: boolean;
  validateVoucher?: boolean;
};

export type PromotionsContextValue = {
  // === Selection state (persisted to splash::promotions::selection) ===
  selectedBoost: PublicBoost | null;
  selectedVoucher: VoucherResponseDto | null;

  // === High-level user actions (analytics + navigation + voucher-type routing) ===
  /**
   * Toggle-with-side-effects.
   * - If the same boost is currently selected → remove + analytics(remove).
   * - Else → apply (clears any voucher) + analytics(apply) + navigate to the
   *   QuickPicks board if the user isn't already there.
   */
  selectBoost: (boost: PublicBoost, location: PromotionActionLocation) => void;

  /**
   * Type-aware voucher selection.
   * - CONTEST_ID_RESTRICTED with a contest id → navigate to the contest, no state change.
   * - QUICKPICKS → toggle selection + analytics + navigate to QP board on apply.
   * - Anything else → no-op.
   */
  selectVoucher: (voucher: VoucherResponseDto, location: PromotionActionLocation) => void;

  selectDepositBonus: (bonus: BonusDefinition, location: PromotionActionLocation) => void;

  // === Bulk cleanup ===
  /** Clears both selections without analytics or navigation. Used by post-entry-success cleanup. */
  clearSelectedBonus: () => void;

  // === Low-level state mutators (no current callers; kept for future surfaces) ===
  /** Removes the selected boost without firing analytics or navigating. */
  removeBoost: () => void;
  /** Removes the selected voucher without firing analytics or navigating. */
  removeVoucher: () => void;

  // === Business actions ===
  validatePromotion: (options?: ValidatePromotionOptions) => Promise<PromotionValidationErrors>;
};

export const PromotionsContext = createContext<PromotionsContextValue | null>(null);

export function PromotionsContextProvider({ children }: { children: ReactNode }) {
  const value = usePromotionsProvider();
  return <PromotionsContext.Provider value={value}>{children}</PromotionsContext.Provider>;
}

export const usePromotionsContext = (): PromotionsContextValue => {
  const ctx = useContext(PromotionsContext);
  if (!ctx) {
    throw new Error(
      'usePromotionsContext must be used within a PromotionsContextProvider — mount it in AppProviders',
    );
  }
  return ctx;
};
